<template>
    <div class="overflow-x-auto bg-white" :class="className">
        <table class="min-w-full">
            <thead>
                <tr class="border-b border-gray-100 bg-gray-100 py-2">
                    <th v-for="column in columns" :key="String(column.key)" :class="[
                        '!h-9 px-6 py-4 text-left select-none text-14px font-semibold text-gray-700',
                        column.sortable ? 'cursor-pointer' : '',
                        column.className || '',
                    ]" :style="{
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                    }" @click="column.sortable ? handleSort(column.key) : undefined">
                        <span class="flex items-center gap-1">
                            {{ column.header }}
                            <div v-if="column.sortable" :class="[
                                'transition-transform duration-200',
                                sortKey === column.key && sortOrder === 'desc'
                                    ? 'rotate-180'
                                    : '',
                            ]">
                                <Icon icon-name="SortIcon" :class="[
                                    sortKey === column.key ? 'opacity-100' : 'opacity-30',
                                ]" />
                            </div>
                        </span>
                    </th>
                    <th v-if="renderActions"
                        class="!h-9 px-6 py-4 text-left text-14px font-semibold text-gray-700 tracking-wide select-none">
                        {{ actionsHeader }}
                    </th>
                </tr>
            </thead>

            <tbody>
                <Loader v-if="loading" :col-span="totalColumns" :table-mode="true" />
                <EmptyState v-else-if="!hasData" :col-span="totalColumns" :message="emptyMessage" />
                <tr v-else v-for="(row, rowIndex) in sortedData" :key="rowIndex"
                    class="border-b border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                    @click="$emit('rowClick', { row, rowIndex })">
                    <td v-for="column in columns" :key="String(column.key)" :class="[
                        '!h-9 px-6 py-4 whitespace-nowrap text-14px text-gray-800 font-normal align-middle',
                        column.className || '',
                    ]" :style="{
                        verticalAlign: 'middle',
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                    }">
                        <slot name="cell" :column="column" :row="row" :rowIndex="rowIndex">
                            <template v-if="column.render">
                                <component v-if="typeof column.render({ row, rowIndex }) === 'object' && column.render({ row, rowIndex }).component" :is="column.render({ row, rowIndex }).component" v-bind="column.render({ row, rowIndex }).props" />
                                <span v-else v-html="column.render({ row, rowIndex })"></span>
                            </template>
                            <span v-else>{{
                                formatCellValue(getRowValue(row, column.key))
                            }}</span>
                        </slot>
                    </td>

                    <td v-if="renderActions"
                        class="px-6 py-2.5 whitespace-nowrap text-14px text-blue-500 font-semibold align-middle">
                        <component :is="renderActions({ row, rowIndex })" />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Loader from './Loader.vue';
import EmptyState from './EmptyState.vue';
import Icon from './Icon.vue';
import Capsule from './Capsule.vue';
import Tag from './Tag.vue';

export default {
    components: {
        Loader,
        EmptyState,
        Icon,
        Capsule,
        Tag,
    },
    props: {
        columns: {
            type: Array,
            required: true,
        },
        data: {
            type: Array,
            required: true,
        },
        actionsHeader: {
            type: String,
            default: 'Actions',
        },
        renderActions: {
            type: Function,
            default: undefined,
        },
        className: {
            type: String,
            default: '',
        },
        initialSortKey: {
            type: [String, Number],
            default: undefined,
        },
        initialSortOrder: {
            type: String,
            default: 'asc',
        },
        onSortChange: {
            type: Function,
            default: undefined,
        },
        emptyMessage: {
            type: String,
            default: 'No data found',
        },
        sortMode: {
            type: String,
            default: 'internal',
        },
        onApiSortChange: {
            type: Function,
            default: undefined,
        },
        loading: {
            type: Boolean,
            default: false,
        },

    },
    data() {
        return {
            sortKey: this.initialSortKey,
            sortOrder: this.initialSortOrder,
        }
    },
    computed: {
        normalizedColumns() {
            return this.columns;
        },
        normalizedData() {
            return this.data;
        },
        totalColumns() {
            return this.normalizedColumns.length + (this.renderActions ? 1 : 0);
        },
        hasData() {
            return this.sortedData.length > 0;
        },
        sortedData() {
            const data = this.normalizedData;
            if (this.sortMode === 'api') return data;
            if (!this.sortKey) return data;
            return this.sortData(data, this.sortKey, this.sortOrder, this.normalizedColumns);
        }
    },
    watch: {
        initialSortKey(newKey) {
            this.sortKey = newKey;
        },
        initialSortOrder(newOrder) {
            this.sortOrder = newOrder;
        }
    },
    methods: {
        formatCellValue(value) {
            if (value == null || value === '' || value === undefined) {
                return '-';
            }
            return String(value);
        },
        getRowValue(row, key) {
            return row[String(key)];
        },
        sortData(data, sortKey, sortOrder, columns) {
            const key = String(sortKey);
            const column = columns.find((col) => String(col.key) === key);
            if (!column?.sortable) return data;

            return [...data].sort((a, b) => {
                const aValue = a[key];
                const bValue = b[key];

                if (aValue === bValue) return 0;
                if (aValue == null) return 1;
                if (bValue == null) return -1;

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
                }

                const comparison = String(aValue).localeCompare(String(bValue));
                return sortOrder === 'asc' ? comparison : -comparison;
            });
        },
        handleSort(key) {
            const keyStr = String(key);
            const newOrder =
                String(this.sortKey) === keyStr && this.sortOrder === 'asc'
                    ? 'desc'
                    : 'asc';

            this.sortKey = key;
            this.sortOrder = newOrder;

            if (this.sortMode === 'api') {
                const sortParams = {};
                if (newOrder === 'desc') {
                    sortParams.sort = `-${keyStr}`;
                } else {
                    sortParams.sort = keyStr;
                }

                this.$emit('apiSortChange', sortParams);
                this.onApiSortChange?.(sortParams);
            } else {
                this.$emit('sortChange', keyStr, newOrder);
                this.onSortChange?.(keyStr, newOrder);
            }
        }
    }

}
</script>

<style scoped>
.text-14px {
    font-size: 1rem;
}
</style>
